import { NodeSelection, PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Command, NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { resetSelection } from './actions';

const pluginName = 'legacyContentMacroPlugin';
const pluginKey = new PluginKey<LegacyContentMacroPluginState>(pluginName);

type LegacyContentMacroPluginState = { arrowKeyPressed: string | null | undefined };

const LEGACY_CONTENT_MACRO_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const LEGACY_CONTENT_MACRO_EXTENSION_KEY = 'legacy-content';

const isLegacyContentMacroExtension = (extensionNode: PMNode) =>
	extensionNode.type.name === 'extension' &&
	extensionNode.attrs?.extensionType === LEGACY_CONTENT_MACRO_EXTENSION_TYPE &&
	extensionNode.attrs?.extensionKey === LEGACY_CONTENT_MACRO_EXTENSION_KEY;

export type LegacyContentMacroPlugin = NextEditorPlugin<
	typeof pluginName,
	{
		sharedState:
			| {
					arrowKeyPressed: string | null | undefined;
			  }
			| undefined;
		actions: {
			resetSelection: (selectionPos?: number) => Command;
		};
	}
>;

export const legacyContentMacroPlugin: LegacyContentMacroPlugin = () => {
	return {
		name: pluginName,
		pmPlugins() {
			return [
				{
					name: pluginName,
					plugin: () => {
						return new SafePlugin<LegacyContentMacroPlugin>({
							key: pluginKey,
							state: {
								init() {
									return {
										arrowKeyPressed: null,
									};
								},
								apply: (tr: Transaction, currentPluginState: LegacyContentMacroPluginState) => {
									const meta = tr.getMeta(pluginKey);
									if (meta) {
										return {
											...currentPluginState,
											...meta,
										};
									}
									return currentPluginState;
								},
							},
							props: {
								handleDOMEvents: {
									// If the user has selected the LCM node and clicks an arrow, save in state what arrow they clicked to use for navigation logic in the extension-handler.
									keydown(view: EditorView, event: KeyboardEvent) {
										if (
											event instanceof KeyboardEvent &&
											['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
										) {
											const { state, dispatch } = view;
											if (
												state.selection instanceof NodeSelection &&
												isLegacyContentMacroExtension(state.selection.node)
											) {
												dispatch(
													state.tr.setMeta(pluginKey, {
														arrowKeyPressed: event.key,
													}),
												);
												event.preventDefault();
											} else {
												// Reset the arrowKeyPressed value if the user is not selecting the LCM node.
												const pluginState = pluginKey.getState(state);
												if (pluginState?.arrowKeyPressed) {
													dispatch(
														state.tr.setMeta(pluginKey, {
															arrowKeyPressed: null,
														}),
													);
												}
											}
										}
										return false;
									},
								},
							},
						});
					},
				},
			];
		},
		actions: {
			resetSelection,
		},

		getSharedState(state: EditorState | undefined) {
			if (!state) {
				return undefined;
			}
			const { arrowKeyPressed } = pluginKey.getState(state) ?? {};

			return {
				arrowKeyPressed,
			};
		},
	};
};
